import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import BarChart from '../../atoms/BarChart/BarChart';
import ChartNoDataState from '../../atoms/ChartNoDataState/ChartNoDataState';
import Slider from '../../atoms/Slider/Slider';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import { EnhancedBarChartProps } from './EnhancedBarChart.types';
import { ChartConfig } from '../../../utils/BaseChart/BaseChartOptions';
import { transformVizDataToBarChart } from '../../../utils/transformVizData';
import { useChartFilters } from './useChartFilters';
import {
    getFirstEnabledZOptionValue,
    isZLevelAllSelected,
    zAxisSelectTagRender,
} from './zAxisFilterSelectTag';
import ChartFilterBar, { ChartFilterColumn, CHART_FILTER_SELECT_CLASSNAME } from './ChartFilterBar';
import { chartMultiSelectTagRender } from './chartFilterMultiTagRender';
import {
    buildY1GroupedOptions,
    buildY2GroupedOptions,
    chartFilterDropdownRender,
    getSelectableOptionValues,
    getYAxisSelectAllValues,
} from './chartFilterDropdownRender';
import {
    formatNumWithKMB,
    formatNumWithKMBTwoDecimalPlaces,
    CHART_FONT_FAMILY_CSS,
} from '../../../utils/formatters';
import { getCategoryAxisLabelTransforms } from '../../../utils/chartAxisLabelTransforms';
import {
    BAR_VALUE_LABEL_DOMAIN_PADDING,
    computeSingleAxisDomain,
    computeZeroAlignedDomains,
} from '../../../utils/chartAxisAlignment';
import {
    getAggregatedChartTooltipInteraction,
    getChartColumnHighlightInteraction,
} from '../../../utils/chartAggregatedTooltip';
import ChartLegendOverflow from '../ChartLegendOverflow/ChartLegendOverflow';
import { buildYZSeriesKey, matchLongestMetricLabel } from '../../../utils/chartSeriesKey';
import {
    computeMissingBarPlaceholderValue,
    fillMissingBarSeriesPlaceholders,
    MISSING_BAR_PLACEHOLDER_FALLBACK_DOMAIN,
    type BarSeriesDatum,
} from './missingBarPlaceholders';

/** Top canvas margin for vertical bar value labels (pairs with `BAR_VALUE_LABEL_DOMAIN_PADDING`). */
const VERTICAL_BAR_LABEL_PADDING_TOP = 8;
const VERTICAL_BAR_LABEL_INSET_TOP = 16;
/** Mirror of `INSET_TOP` reserved at the plot bottom when negatives are present so the
 *  negative bar value label (rendered with `dy: 8` below the bar) never sits on the x-axis. */
const VERTICAL_BAR_LABEL_INSET_BOTTOM = 28;

/**
 * Small symmetric floor applied to `insetLeft` / `insetRight` of the value axis in
 * horizontal mode. We deliberately do NOT scale these insets to the longest formatted
 * bar value label: G2 maps the value-axis ticks into the inset-shrunk data rectangle
 * but renders the bars in the full plot rectangle, so any meaningful inset visibly
 * drifts the value `0` tick away from where the bars actually start. Bar value label
 * clearance is instead provided by value-axis domain padding from
 * `computeSingleAxisDomain` / `computeZeroAlignedDomains` (`BAR_VALUE_LABEL_DOMAIN_PADDING`),
 * which keeps the outermost bars short of the plot edge in both orientations.
 */
const HORIZONTAL_INSET_RIGHT_FLOOR = 8;

/**
 * Left-gutter reserves for the transposed category (visual Y) axis. `paddingLeft` is
 * passed to G2 as a fixed number, so it — not the axis `size` — decides how much room the
 * category labels actually get before clipping at the canvas edge. Both reserves are
 * therefore derived from the SAME `horizontalCategoryAxisPx` used for the axis `size`, so
 * the reserved padding always fully contains the axis. `SCROLLBAR_RESERVE` additionally
 * clears the overlaid vertical scrollbar column; `LABEL_GUTTER` is a small breathing gap.
 * (The previous non-scroll formula reserved `axisPx − 20`, which clipped long labels while
 * the scroll path — `axisPx + reserve` — stayed clear; hence scroll worked but non-scroll did not.)
 */
const HORIZONTAL_CATEGORY_SCROLLBAR_RESERVE_PX = 56;
const HORIZONTAL_CATEGORY_LABEL_GUTTER_PX = 16;

/** Omit null / undefined / non-finite numbers so @antv/g2 interval marks are not drawn for missing metrics (tooltips were showing 0 via Number(null)). */
function isPlottableBarMetricValue(v: unknown): v is number {
    if (v == null) return false;
    return typeof v === 'number' && Number.isFinite(v);
}

const EnhancedBarChart: React.FC<EnhancedBarChartProps> = (props) => {
    const {
        vizData,
        title: propTitle,
        description,
        data: propData,
        xAxisKey: propXAxisKey,
        yAxisLabel: propYAxisLabel,
        barConfigs: propBarConfigs,
        filters = [],
        showFilters = true,
        legend: propLegend,
        footerLabel,
        showFooterArrow = true,
        onDownload,
        onBarClick,
        onTrackChartEvent,
        sizeAttributes,
        viewLevelAdditionalProps,
        additionalMarks,
        horizontal = false,
        className = '',
        chartClassName = '',
    } = props;

    const xAxisTypeToken = vizData?.xAxisType?.[0]?.trim();
    const xAxisScrollKind = xAxisTypeToken?.toLowerCase();
    const scrollModeFromViz =
        vizData && (xAxisScrollKind === 'category' || xAxisScrollKind === 'time') ? xAxisScrollKind : null;

    const showSlider =
        props.showSlider !== undefined
            ? props.showSlider
            : scrollModeFromViz === 'category'
              ? false
              : scrollModeFromViz === 'time'
                ? true
                : true;

    const showScrollbar =
        props.showScrollbar !== undefined
            ? props.showScrollbar
            : scrollModeFromViz === 'category'
              ? true
              : scrollModeFromViz === 'time'
                ? false
                : false;

    const scrollbarVisibleCount = props.scrollbarVisibleCount ?? 10;

    const theme = useTheme();

    // Use chart filters hook if vizData is provided
    const chartFilters = useChartFilters(vizData);

    /** Secondary axis only applies when there are multiple Y metric families (viz_data). */
    const showSecondaryAxisFilter = useMemo(() => {
        const yOpts = chartFilters.filterOptions?.y;
        if (!yOpts?.length) return false;
        return new Set(yOpts.map((y) => y.familyIndex)).size > 1;
    }, [chartFilters.filterOptions?.y]);

    // Transform viz_data if provided, otherwise use props directly
    const chartConfig = useMemo(() => {
        if (vizData) {
            // Use filtered data and selected metrics
            const filteredVizData = {
                ...vizData,
                data: chartFilters.filteredData,
            };
            const transformed = transformVizDataToBarChart(filteredVizData, chartFilters.selectedMetrics, {
                sortCategoryAxis: horizontal,
            });
            return {
                title: transformed.title,
                data: transformed.data,
                xAxisKey: transformed.xAxisKey,
                yAxisLabel: transformed.yAxisLabel,
                y1AxisLabel: transformed.y1AxisLabel,
                y2AxisLabel: transformed.y2AxisLabel,
                y1BarConfigs: transformed.y1BarConfigs,
                y2BarConfigs: transformed.y2BarConfigs,
                zAxisKey: transformed.zAxisKey,
                legend: {
                    y1: transformed.y1BarConfigs.map((config) => ({
                        label: config.label || config.dataKey,
                        color: config.color,
                    })),
                    y2: transformed.y2BarConfigs.map((config) => ({
                        label: config.label || config.dataKey,
                        color: config.color,
                    })),
                },
            };
        } else {
            // Use props directly (legacy format)
            if (!propData || !propXAxisKey || !propBarConfigs) {
                throw new Error('Either vizData or (data, xAxisKey, barConfigs) must be provided');
            }
            return {
                title: propTitle || '',
                data: propData,
                xAxisKey: propXAxisKey,
                yAxisLabel: propYAxisLabel,
                y1BarConfigs: propBarConfigs,
                y2BarConfigs: [],
                zAxisKey: undefined,
                legend: propLegend
                    ? { y1: propLegend, y2: [] }
                    : {
                          y1: propBarConfigs.map((config) => ({
                              label: config.label || config.dataKey,
                              color: config.color,
                          })),
                          y2: [],
                      },
            };
        }
    }, [
        vizData,
        propTitle,
        propData,
        propXAxisKey,
        propYAxisLabel,
        propBarConfigs,
        propLegend,
        chartFilters.filteredData,
        chartFilters.selectedMetrics,
    ]);

    const { title, data, xAxisKey, yAxisLabel, y1AxisLabel, y2AxisLabel, y1BarConfigs, y2BarConfigs } = chartConfig;

    // ===== Analytics helpers =====
    const chartTitleForTracking = vizData?.chartTitle ?? title ?? propTitle;
    const chartTypeForTracking = vizData?.chart_type ?? (horizontal ? 'HorizontalBar' : 'Bar');

    const trackChartEvent = onTrackChartEvent;

    const trackFilterValueChange = useCallback(
        (filter_name: string, prevValues: unknown[], nextValues: unknown[]) => {
            if (!trackChartEvent) return;
            const prev = new Set(prevValues as Array<string | number>);
            const next = new Set(nextValues as Array<string | number>);
            const added = [...next].filter((v) => !prev.has(v));
            const removed = [...prev].filter((v) => !next.has(v));
            if (added.length > 0) {
                trackChartEvent({
                    type: 'filter_select',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: added,
                });
            }
            if (removed.length > 0) {
                trackChartEvent({
                    type: 'filter_deselect',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: removed,
                });
            }
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const trackFilterDropdownOpen = useCallback(
        (filter_name: string) => {
            trackChartEvent?.({
                type: 'filter_dropdown_open',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                filter_name,
            });
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const uniqueCategories = useMemo(() => Array.from(new Set(data.map((d: any) => d[xAxisKey]))), [data, xAxisKey]);
    const totalCategories = uniqueCategories.length;

    const [sliderRange, setSliderRange] = useState<[number, number]>([0, Math.max(0, totalCategories - 1)]);

    useEffect(() => {
        setSliderRange([0, Math.max(0, totalCategories - 1)]);
    }, [totalCategories]);

    const sliderFilteredCategories = useMemo(
        () => new Set(uniqueCategories.slice(sliderRange[0], sliderRange[1] + 1)),
        [uniqueCategories, sliderRange],
    );

    const filteredCategoryCount = sliderRange[1] - sliderRange[0] + 1;

    /** After transpose, categories use the visual Y-axis (left); reserve enough width so labels are never trimmed. */
    const horizontalCategoryAxisPx = useMemo(() => {
        if (!horizontal || !data?.length) return 140;
        let maxChars = 0;
        for (const row of data) {
            const len = String((row as Record<string, unknown>)[xAxisKey] ?? '').length;
            if (len > maxChars) maxChars = len;
        }
        const estimated = Math.ceil(maxChars * 7.5) + 48;
        return Math.min(Math.max(estimated, showSlider && !showScrollbar ? 120 : 90), 1200);
    }, [horizontal, data, xAxisKey, showSlider, showScrollbar]);

    // Create combined bar configs for y1 and y2 axes so they dodge together
    const barsConfig: ChartConfig[] = [];

    // Generate colors for all unique series combinations
    // First, determine all unique series that will be created
    const allBarConfigs = [...y1BarConfigs, ...y2BarConfigs];
    const uniqueZSeries = Array.from(
        new Set(data.map((item: any) => item.zSeriesKey).filter((z): z is string => Boolean(z))),
    );
    const uniqueSeries: string[] = [];
    allBarConfigs.forEach((config) => {
        const label = config.label || config.dataKey;
        if (uniqueZSeries.length > 0) {
            uniqueZSeries.forEach((zSeries: string) => {
                uniqueSeries.push(`${label}-${zSeries}`);
            });
        } else {
            uniqueSeries.push(label);
        }
    });

    // Create color map for series
    const DEFAULT_COLORS = [
        '#5B50D1',
        '#DBC7A4',
        '#4ECDC4',
        '#FF6B6B',
        '#95E1D3',
        '#F38181',
        '#AA96DA',
        '#FCBAD3',
        '#A8D8EA',
        '#FFD93D',
    ];
    const seriesColorMap = new Map<string, string>();
    const metricLabelsForMatch = allBarConfigs.map((c) => c.label || c.dataKey);
    uniqueSeries.forEach((series, index) => {
        const baseMetric = matchLongestMetricLabel(series, metricLabelsForMatch);
        const baseConfig = baseMetric ? allBarConfigs.find((c) => (c.label || c.dataKey) === baseMetric) : undefined;
        if (baseConfig && series === baseMetric && baseConfig.color) {
            seriesColorMap.set(series, baseConfig.color);
        } else {
            seriesColorMap.set(series, DEFAULT_COLORS[index % DEFAULT_COLORS.length]);
        }
    });

    const chartLegendItems = uniqueSeries.map((series) => ({
        key: series,
        label: series,
        color: seriesColorMap.get(series) || '#5B50D1',
    }));

    /**
     * `scrollbarVisibleCount` is a budget on the *total* visible bars. Each x-axis
     * category renders one bar per series (metric × Z), so the effective number of
     * categories the scrollbar window exposes is the budget divided by series count.
     * This keeps the bar density roughly constant as users add/remove metrics or
     * Z-series instead of doubling the bar count when a second metric is selected.
     * At least one category is always shown to avoid an empty viewport.
     */
    const barsPerCategory = Math.max(1, uniqueSeries.length);
    const effectiveScrollbarVisibleCount = Math.max(
        1,
        Math.floor(scrollbarVisibleCount / barsPerCategory),
    );

    /**
     * The scrollbar only needs to be configured (and rendered) when more categories exist than
     * fit in the viewport. When all categories fit, passing a scrollbar config to G2 still
     * runs its scrollbar-aware layout pass, which mis-sizes the value-axis viewport on the
     * initial render — the value `0` tick drifts inwards until the user nudges the scrollbar
     * (which emits `scrollbarX:filter` and forces G2 to relayout). Gating on `needsScroll`
     * keeps the layout path identical to the no-scrollbar case when nothing is scrollable.
     */
    const needsScroll = showScrollbar && filteredCategoryCount > effectiveScrollbarVisibleCount;
    const maxScrollOffset = Math.max(0, filteredCategoryCount - effectiveScrollbarVisibleCount);

    const effectiveLegendItems =
        !vizData && propLegend !== undefined
            ? propLegend.length === 0
                ? []
                : propLegend.map((item, index) => ({
                      key: `${item.label}-${index}`,
                      label: item.label,
                      color: item.color,
                  }))
            : chartLegendItems;

    // Combine all data into a single dataset with axisGroup identifier
    // This allows us to create a single bar config that includes all bars for proper dodging
    // Each unique combination of metric + Z values creates a separate series
    const combinedData = data.flatMap((item) => {
        const y1Data = y1BarConfigs.flatMap((config) => {
            const raw = item[config.dataKey];
            if (!isPlottableBarMetricValue(raw)) return [];
            const metricLabel = config.label || config.dataKey;
            // If Z-axis values exist, combine metric label with Z series key to create unique series
            const seriesKey = item.zValues?.length
                ? buildYZSeriesKey(metricLabel, item.zValues as string[])
                : metricLabel;

            // Get color for this series
            const seriesColor = seriesColorMap.get(seriesKey) || config.color || '#5B50D1';

            return [
                {
                    ...item,
                    value: raw,
                    metricName: seriesKey, // Use combined series key for unique series identification
                    metricLabel: metricLabel, // Keep original metric label for reference
                    configColor: seriesColor, // Use series-specific color
                    axisGroup: 'y1',
                    dataKey: config.dataKey,
                    // Create a shared group key for dodging - combines x value with a position indicator
                    dodgeGroup: `${item[xAxisKey]}_y1`,
                },
            ];
        });

        const y2Data = y2BarConfigs.flatMap((config) => {
            const raw = item[config.dataKey];
            if (!isPlottableBarMetricValue(raw)) return [];
            const metricLabel = config.label || config.dataKey;
            // If Z-axis values exist, combine metric label with Z series key to create unique series
            const seriesKey = item.zValues?.length
                ? buildYZSeriesKey(metricLabel, item.zValues as string[])
                : metricLabel;

            // Get color for this series
            const seriesColor = seriesColorMap.get(seriesKey) || config.color || '#5B50D1';

            return [
                {
                    ...item,
                    value: raw,
                    metricName: seriesKey, // Use combined series key for unique series identification
                    metricLabel: metricLabel, // Keep original metric label for reference
                    configColor: seriesColor, // Use series-specific color
                    axisGroup: 'y2',
                    dataKey: config.dataKey,
                    // Create a shared group key for dodging - combines x value with a position indicator
                    dodgeGroup: `${item[xAxisKey]}_y2`,
                },
            ];
        });

        return [...y1Data, ...y2Data];
    });

    // Calculate y min/max for both axes (real data only — placeholders must not affect domain)
    let y1MinMax: { min: number; max: number } | undefined;
    let y2MinMax: { min: number; max: number } | undefined;

    if (y1BarConfigs.length > 0) {
        const y1Values = combinedData
            .filter((d) => d.axisGroup === 'y1')
            .map((d) => d.value)
            .filter((v) => v != null && !isNaN(v));
        if (y1Values.length > 0) {
            y1MinMax = {
                min: Math.min(...y1Values),
                max: Math.max(...y1Values),
            };
        }
    }

    if (y2BarConfigs.length > 0) {
        const y2Values = combinedData
            .filter((d) => d.axisGroup === 'y2')
            .map((d) => d.value)
            .filter((v) => v != null && !isNaN(v));
        if (y2Values.length > 0) {
            y2MinMax = {
                min: Math.min(...y2Values),
                max: Math.max(...y2Values),
            };
        }
    }

    // Each axis's placeholder magnitude must be sized off its OWN extent, in its own native
    // units — a shared value derived from whichever axis has the bigger magnitude renders as a
    // huge bar once inserted (unscaled) into whichever axis is actually the smaller one.
    const y1PlaceholderValue = computeMissingBarPlaceholderValue(y1MinMax, undefined);
    const y2PlaceholderValue = computeMissingBarPlaceholderValue(undefined, y2MinMax);
    const filledCombinedData = fillMissingBarSeriesPlaceholders(combinedData as BarSeriesDatum[], {
        categories: uniqueCategories.map(String),
        categoryRecords: data as Array<Record<string, unknown>>,
        xAxisKey,
        y1BarConfigs: y1BarConfigs as Array<{ dataKey: string; color: string; label?: string }>,
        y2BarConfigs: y2BarConfigs as Array<{ dataKey: string; color: string; label?: string }>,
        uniqueZSeries,
        seriesColorMap,
        y1PlaceholderValue,
        y2PlaceholderValue,
    });

    const valueAxisDomainPadding = BAR_VALUE_LABEL_DOMAIN_PADDING;

    // Zero-aligned domains for dual axes (both zeros at the same visual position)
    let alignedDomains: { y1: [number, number]; y2: [number, number] } | undefined;
    if (y1BarConfigs.length > 0 && y2BarConfigs.length > 0 && y1MinMax && y2MinMax) {
        alignedDomains = computeZeroAlignedDomains(
            y1MinMax.min, y1MinMax.max,
            y2MinMax.min, y2MinMax.max,
            valueAxisDomainPadding,
        );
    }

    /**
     * Pre-compute a stable domain for the main (visual y1) scale when only a single axis
     * is selected. Without this, G2 auto-fits the y scale to whatever rows are visible in
     * the slider/scrollbar window, so the bar heights re-scale every time the user
     * scrolls. With both axes selected the dual-axis path already pins the domain via
     * `alignedDomains`, so this only kicks in for the single-axis case.
     *
     * Bars in single-axis mode are not normalized (see `normalizedData` below), so the
     * domain matches the actual `value` channel: raw y1 values when only y1 is selected,
     * raw y2 values when only y2 is selected.
     */
    let singleAxisDomain: [number, number] | undefined;
    if (!alignedDomains) {
        if (y1BarConfigs.length > 0 && y1MinMax) {
            singleAxisDomain = computeSingleAxisDomain(y1MinMax.min, y1MinMax.max, valueAxisDomainPadding);
        } else if (y2BarConfigs.length > 0 && y2MinMax) {
            singleAxisDomain = computeSingleAxisDomain(y2MinMax.min, y2MinMax.max, valueAxisDomainPadding);
        } else if (y1BarConfigs.length > 0 || y2BarConfigs.length > 0) {
            // Configured axis has no real data at all — every bar is a missing-series placeholder.
            // Pin a domain so G2 doesn't autofit to the placeholder value itself (see
            // MISSING_BAR_PLACEHOLDER_FALLBACK_DOMAIN).
            singleAxisDomain = MISSING_BAR_PLACEHOLDER_FALLBACK_DOMAIN;
        }
    }

    // Normalize Y2 values to Y1 scale for visual positioning
    // Uses zero-aligned domains so that 0 on Y2 maps to 0 on Y1
    let normalizedData = filledCombinedData;
    if (alignedDomains) {
        const [aY1Min, aY1Max] = alignedDomains.y1;
        const [aY2Min, aY2Max] = alignedDomains.y2;
        const aY1Range = aY1Max - aY1Min || 1;
        const aY2Range = aY2Max - aY2Min || 1;

        normalizedData = filledCombinedData.map((d) => {
            if (d.axisGroup === 'y2' && d.value != null && !isNaN(d.value)) {
                const normalized = ((d.value - aY2Min) / aY2Range) * aY1Range + aY1Min;
                return {
                    ...d,
                    value: normalized,
                    originalValue: d.isMissingPlaceholder ? undefined : d.value,
                };
            }
            return {
                ...d,
                originalValue: d.isMissingPlaceholder ? undefined : d.value,
            };
        });
    }

    const hasNegativeValues = combinedData.some(
        (d) => typeof d.value === 'number' && Number.isFinite(d.value) && d.value < 0,
    );

    // Create bar configs - use a single mark with all bars for proper dodging
    // This ensures Y1 and Y2 bars dodge together for the same x value
    if (normalizedData.length > 0) {
        // Create a single combined bar config with all bars
        // This is the key: all bars in one mark so they dodge together
        const allBarsConfig: ChartConfig = {
            encode: {
                x: xAxisKey,
                y: 'value',
                series: 'metricName',
                color: 'configColor',
            },
            scale: {
                color: { type: 'identity' },
                x: { padding: horizontal ? 0.3 : 0.7 },
                ...(alignedDomains
                    ? { y: { domain: alignedDomains.y1 } }
                    : singleAxisDomain
                      ? { y: { domain: singleAxisDomain } }
                      : {}),
            },
            // Use dodgeX with groupBy to group by axisGroup first, then metricName
            // This ensures Y1 and Y2 bars are visually separated but dodged together
            transform: [
                {
                    type: 'dodgeX',
                    groupBy: ['axisGroup', 'metricName'],
                },
            ],
            style: {
                radiusTopLeft: (d: any) => {
                    if (d.isMissingPlaceholder) return 2;
                    const barValue = d.originalValue !== undefined ? d.originalValue : d.value;
                    return barValue != null && !isNaN(barValue) && barValue > 0 ? 4 : 0;
                },
                radiusTopRight: (d: any) => {
                    if (d.isMissingPlaceholder) return 2;
                    const barValue = d.originalValue !== undefined ? d.originalValue : d.value;
                    return barValue != null && !isNaN(barValue) && barValue > 0 ? 4 : 0;
                },
                radiusBottomLeft: (d: any) => {
                    if (d.isMissingPlaceholder) return 0;
                    const barValue = d.originalValue !== undefined ? d.originalValue : d.value;
                    return barValue != null && !isNaN(barValue) && barValue < 0 ? 4 : 0;
                },
                radiusBottomRight: (d: any) => {
                    if (d.isMissingPlaceholder) return 0;
                    const barValue = d.originalValue !== undefined ? d.originalValue : d.value;
                    return barValue != null && !isNaN(barValue) && barValue < 0 ? 4 : 0;
                },
                fill: 'configColor',
                fillOpacity: (d: any) => (d.isMissingPlaceholder ? 0.45 : 1),
                cursor: 'pointer',
            },
            axis: {
                x: {
                    // With Y2, G2 merges axes from two marks and the view-level `axis.x.title: false`
                    // could suppress the category title; set title on the view instead (see below).
                    title: y2BarConfigs.length > 0 ? false : xAxisKey,
                    size: horizontal ? horizontalCategoryAxisPx : 100,
                    ...(horizontal
                        ? {
                              labelAutoWrap: false,
                              // Hide overlapping category ticks when many rows; each shown label stays full text (no ellipsis).
                              labelAutoHide: true,
                              labelFormatter: (datum: unknown) => String(datum ?? ''),
                          }
                        : {
                              /**
                               * Wrap onto up to three lines at the tick space, then let G2 ellipsise
                               * against the labels' real measured bounds. Pre-truncating in a
                               * `labelFormatter` instead would discard the full category name, which
                               * `attachAxisLabelHoverTooltip` reads back off the label shape to show
                               * on hover. `labelAuto*` flags are absent on purpose — a non-empty
                               * `transform` makes G2 ignore them.
                               */
                              transform: getCategoryAxisLabelTransforms(),
                          }),
                    labelFontSize: 13,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    labelStroke: '#000',
                    labelOpacity: 0.4,
                    labelFontWeight: 400,
                },
                y: {
                    position: horizontal ? ('top' as const) : ('left' as const),
                    grid: true,
                    title: null,
                    gridStrokeOpacity: 0.2,
                    labelFormatter: (datum: any) => formatNumWithKMB(Number(datum)),
                    labelAutoRotate: false,
                    labelFontSize: 13,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    labelStroke: '#000',
                    labelOpacity: 0.4,
                    labelFontWeight: 400,
                },
            },
            tooltip: {
                title: (d: any) => String(d[xAxisKey] ?? ''),
                items: [
                    (d: any) => {
                        const displayValue = d.originalValue !== undefined ? d.originalValue : d.value;
                        const name = d.metricName || d.metricLabel || 'Value';
                        const color = d.configColor as string | undefined;
                        if (d.isMissingPlaceholder || !isPlottableBarMetricValue(displayValue)) {
                            return { name, value: '—', color };
                        }
                        return {
                            name,
                            value: formatNumWithKMB(displayValue),
                            color,
                        };
                    },
                ],
            },
            labels: horizontal
                ? [
                      {
                          text: (d: any) => {
                              if (d.isMissingPlaceholder) return '';
                              const val = d.originalValue !== undefined ? d.originalValue : d.value;
                              return val != null && !isNaN(val) && val >= 0
                                  ? formatNumWithKMBTwoDecimalPlaces(val)
                                  : '';
                          },
                          position: 'right',
                          textAlign: 'left',
                          textBaseline: 'middle',
                          dx: 10,
                          fill: 'rgba(0, 0, 0, 0.65)',
                          fontSize: 12,
                          fontFamily: CHART_FONT_FAMILY_CSS,
                          transform: [{ type: 'overlapHide' }],
                      },
                      {
                          text: (d: any) => {
                              if (d.isMissingPlaceholder) return '';
                              const val = d.originalValue !== undefined ? d.originalValue : d.value;
                              return val != null && !isNaN(val) && val < 0
                                  ? formatNumWithKMBTwoDecimalPlaces(val)
                                  : '';
                          },
                          position: 'left',
                          textAlign: 'right',
                          textBaseline: 'middle',
                          dx: -10,
                          fill: 'rgba(0, 0, 0, 0.65)',
                          fontSize: 12,
                          fontFamily: CHART_FONT_FAMILY_CSS,
                          transform: [{ type: 'overlapHide' }],
                      },
                  ]
                : [
                      {
                          text: (d: any) => {
                              if (d.isMissingPlaceholder) return '';
                              const val = d.originalValue !== undefined ? d.originalValue : d.value;
                              return val != null && !isNaN(val) && val >= 0
                                  ? formatNumWithKMBTwoDecimalPlaces(val)
                                  : '';
                          },
                          position: 'top',
                          textBaseline: 'bottom',
                          dy: -8,
                          fill: 'rgba(0, 0, 0, 0.65)',
                          fontSize: 12,
                          fontFamily: CHART_FONT_FAMILY_CSS,
                          transform: [{ type: 'overlapHide' }],
                      },
                      {
                          text: (d: any) => {
                              if (d.isMissingPlaceholder) return '';
                              const val = d.originalValue !== undefined ? d.originalValue : d.value;
                              return val != null && !isNaN(val) && val < 0
                                  ? formatNumWithKMBTwoDecimalPlaces(val)
                                  : '';
                          },
                          position: 'bottom',
                          textBaseline: 'top',
                          dy: 8,
                          fill: 'rgba(0, 0, 0, 0.65)',
                          fontSize: 12,
                          fontFamily: CHART_FONT_FAMILY_CSS,
                          transform: [{ type: 'overlapHide' }],
                      },
                  ],
            data: needsScroll
                ? normalizedData.filter((d: any) => sliderFilteredCategories.has(d[xAxisKey]))
                : normalizedData,
            ...(needsScroll
                ? {
                      additionalConfig: {
                          scrollbar: {
                              x: {
                                  ratio: Math.min(1, effectiveScrollbarVisibleCount / Math.max(1, filteredCategoryCount)),
                                  trackSize: 8,
                                  thumbFill: '#5B50D1',
                                  thumbFillOpacity: 0.45,
                                  trackFill: '#f0f0f0',
                                  trackFillOpacity: 1,
                                  isRound: true,
                                  /**
                                   * Horizontal mode applies coordinate transpose, so the categorical
                                   * x-channel is rendered vertically. Pin the scrollbar to the left so
                                   * it sits next to the category axis labels (mirroring the bottom
                                   * scrollbar in vertical mode).
                                   */
                                  ...(horizontal && { position: 'left' as const }),
                              },
                          },
                      },
                  }
                : showSlider
                  ? {
                        additionalConfig: {
                            slider: {
                                x: {
                                    selectionFill: '#F7F5FF',
                                    selectionFillOpacity: 1,
                                },
                            },
                        },
                    }
                  : {}),
        };

        barsConfig.push(allBarsConfig);

        if (y2BarConfigs.length > 0 && y2MinMax) {
            const y2DataForAxis = normalizedData.filter((d) => d.axisGroup === 'y2');
            const y2AxisDomain: [number, number] = alignedDomains
                ? alignedDomains.y2
                : computeSingleAxisDomain(y2MinMax.min, y2MinMax.max, valueAxisDomainPadding);

            const y2AxisConfig: ChartConfig = {
                encode: {
                    x: xAxisKey,
                    y: 'originalValue',
                    // Without this, elements from this mark carry no `series` value, so G2's
                    // shared-tooltip hit-testing (which offsets each bar by `series * bandWidth`
                    // to sort them for bisector search) computes NaN for these elements. That NaN
                    // corrupts the sort order of ALL elements in the view once a secondary axis is
                    // present, making the tooltip resolve to the wrong (often first) category past
                    // roughly the midpoint of the plot. Same domain as the main mark, so it adds no
                    // new series values — this mark stays fully transparent either way.
                    series: 'metricName',
                },
                scale: {
                    y: {
                        independent: true,
                        domain: y2AxisDomain,
                    },
                },
                style: {
                    fill: 'transparent',
                    stroke: 'transparent',
                    opacity: 0,
                },
                axis: {
                    y: {
                        position: horizontal ? ('bottom' as const) : ('right' as const),
                        grid: false,
                        title: null,
                        gridStrokeOpacity: 0.2,
                        labelFormatter: (datum: any) => {
                            const num = Number(datum);
                            if (num === 0) return '0';
                            return formatNumWithKMB(num);
                        },
                        labelAutoRotate: false,
                        labelFontSize: 13,
                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                        labelStroke: '#000',
                        labelOpacity: 0.4,
                        labelFontWeight: 400,
                    },
                },
                data: y2DataForAxis,
                tooltip: false,
            };

            barsConfig.push(y2AxisConfig);
        }
    }

    // Use original data (the bar configs have their own data property)
    const transformedData = data;

    const chartTheme = useMemo(
        () => ({
            ...theme,
            components: {
                ...theme?.components,
                Select: {
                    ...(theme as { components?: { Select?: object } })?.components?.Select,
                    colorTextPlaceholder: 'rgba(0,0,0,0.7)',
                },
            },
        }),
        [theme],
    );

    const chartRef = useRef<Chart | null>(null);
    const chartWrapperRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const scrollOffsetRef = useRef(0);
    const lastScrollTimeRef = useRef(0);
    const SCROLL_THROTTLE_MS = 150;

    const scrollToOffset = useCallback(
        (targetOffset: number) => {
            const chart = chartRef.current;
            if (!chart || !needsScroll) return;

            const next = Math.max(0, Math.min(maxScrollOffset, Math.round(targetOffset)));
            const prev = scrollOffsetRef.current;
            if (next === prev) return;
            scrollOffsetRef.current = next;

            const absStart = sliderRange[0] + next;
            const absEnd = Math.min(absStart + effectiveScrollbarVisibleCount - 1, sliderRange[1]);
            const startCat = uniqueCategories[absStart];
            const endCat = uniqueCategories[absEnd];

            chart.emit('scrollbarX:filter', {
                data: { selection: [[startCat, endCat], undefined] },
            });
        },
        [needsScroll, maxScrollOffset, effectiveScrollbarVisibleCount, sliderRange, uniqueCategories],
    );

    const scrollChart = useCallback(
        (delta: number) => {
            scrollToOffset(scrollOffsetRef.current + delta);
        },
        [scrollToOffset],
    );

    const handleSliderChange = useCallback(
        (val: number | number[]) => {
            if (!Array.isArray(val)) return;
            const [start, end] = val as [number, number];
            setSliderRange([start, end]);
            scrollOffsetRef.current = 0;
            trackChartEvent?.({
                type: 'slider_use',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                range: [start, end],
            });
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const handleChartReady = useCallback((chart: Chart) => {
        chartRef.current = chart;
        scrollOffsetRef.current = 0;
    }, []);

    useEffect(() => {
        if (!needsScroll) return;
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;

        const onWheel = (e: WheelEvent) => {
            if (!chartRef.current) return;
            e.preventDefault();
            const now = Date.now();
            if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
            lastScrollTimeRef.current = now;
            const rawDelta = e.deltaX || e.deltaY;
            const direction = rawDelta > 0 ? 1 : -1;
            scrollChart(direction);
            trackChartEvent?.({
                type: 'scroll',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                direction: direction > 0 ? 'forward' : 'backward',
            });
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 1) {
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!touchStartRef.current || e.touches.length !== 1) return;
            const dx = touchStartRef.current.x - e.touches[0].clientX;
            const dy = touchStartRef.current.y - e.touches[0].clientY;
            /**
             * After transpose, categories run vertically, so vertical swipes are the natural
             * scroll direction in horizontal mode (and horizontal swipes in vertical mode).
             */
            const primaryDelta = horizontal ? dy : dx;
            const secondaryDelta = horizontal ? dx : dy;
            if (Math.abs(primaryDelta) > Math.abs(secondaryDelta) && Math.abs(primaryDelta) > 8) {
                e.preventDefault();
                const now = Date.now();
                if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
                lastScrollTimeRef.current = now;
                const direction = primaryDelta > 0 ? 1 : -1;
                scrollChart(direction);
                trackChartEvent?.({
                    type: 'scroll',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    direction: direction > 0 ? 'forward' : 'backward',
                });
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchEnd = () => {
            touchStartRef.current = null;
        };

        wrapper.addEventListener('wheel', onWheel, { passive: false });
        wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
        wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
        wrapper.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            wrapper.removeEventListener('wheel', onWheel);
            wrapper.removeEventListener('touchstart', onTouchStart);
            wrapper.removeEventListener('touchmove', onTouchMove);
            wrapper.removeEventListener('touchend', onTouchEnd);
        };
    }, [needsScroll, scrollChart, horizontal, trackChartEvent, chartTitleForTracking, chartTypeForTracking]);

    return (
        <ConfigProvider theme={chartTheme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-enhanced-barchart
            >
                {/* Header Section */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {title}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Filters Section */}
                {showFilters && (filters.length > 0 || (vizData && chartFilters.filterOptions)) && (
                    <div className="px-5 pt-5 pb-3">
                        <ChartFilterBar>
                            {/* Custom filters from props (legacy support) */}
                            {filters.map((filter, index) => {
                                const filterLabel =
                                    typeof filter.label === 'string' ? filter.label : `filter-${index}`;
                                return (
                                    <ChartFilterColumn key={`custom-${index}`} label={filter.label}>
                                        <Select
                                            value={filter.defaultValue}
                                            onChange={(value) => {
                                                trackFilterValueChange(
                                                    filterLabel,
                                                    filter.defaultValue != null ? [filter.defaultValue] : [],
                                                    value != null ? [value] : [],
                                                );
                                                filter.onChange?.(value);
                                            }}
                                            onDropdownVisibleChange={(open) =>
                                                open && trackFilterDropdownOpen(filterLabel)
                                            }
                                            options={filter.options}
                                            className={CHART_FILTER_SELECT_CLASSNAME}
                                            data-test-id={`${props['data-test-id']}-filter-${index}`}
                                        />
                                    </ChartFilterColumn>
                                );
                            })}

                            {/* Auto-generated filters from vizData */}
                            {vizData && chartFilters.filterOptions && (
                                <>
                                    {/* X-axis filter */}
                                    {chartFilters.filterOptions.x.length > 0 && (
                                        <ChartFilterColumn label={vizData.xAxisTitle[0] || 'X-Axis'}>
                                            <Select
                                                mode="multiple"
                                                value={chartFilters.filters.x}
                                                onChange={(value) => {
                                                    trackFilterValueChange(
                                                        vizData.xAxisTitle[0] || 'X-Axis',
                                                        chartFilters.filters.x,
                                                        value || [],
                                                    );
                                                    chartFilters.updateFilter('x', value);
                                                }}
                                                onDropdownVisibleChange={(open) =>
                                                    open && trackFilterDropdownOpen(vizData.xAxisTitle[0] || 'X-Axis')
                                                }
                                                options={chartFilters.filterOptions.x.map((x) => ({
                                                    label: x,
                                                    value: x,
                                                }))}
                                                tagRender={chartMultiSelectTagRender(chartFilters.filters.x, (v) => v)}
                                                className={CHART_FILTER_SELECT_CLASSNAME}
                                                placeholder="Select..."
                                                data-test-id={`${props['data-test-id']}-filter-x`}
                                                dropdownRender={(menu) =>
                                                    chartFilterDropdownRender(menu, {
                                                        onReset: () => {
                                                            trackFilterValueChange(
                                                                vizData.xAxisTitle[0] || 'X-Axis',
                                                                chartFilters.filters.x,
                                                                [],
                                                            );
                                                            chartFilters.updateFilter('x', []);
                                                        },
                                                        onSelectAll: () => {
                                                            const allX = chartFilters.filterOptions!.x;
                                                            trackFilterValueChange(
                                                                vizData.xAxisTitle[0] || 'X-Axis',
                                                                chartFilters.filters.x,
                                                                allX,
                                                            );
                                                            chartFilters.updateFilter('x', allX);
                                                        },
                                                        dataTestIdPrefix: `${props['data-test-id']}-filter-x`,
                                                    })
                                                }
                                            />
                                        </ChartFilterColumn>
                                    )}

                                    {/* Y1 filter (left) - always show if there are y options */}
                                    {chartFilters.filterOptions.y.length > 0 &&
                                        (() => {
                                            const y1GroupedOptions = buildY1GroupedOptions(
                                                chartFilters.filterOptions.y,
                                                chartFilters.filters.y1,
                                                chartFilters.filters.y2,
                                            );
                                            return (
                                                <ChartFilterColumn label="Primary Axis">
                                                    <Select
                                                        mode="multiple"
                                                        value={chartFilters.filters.y1}
                                                        onDropdownVisibleChange={(open) =>
                                                            open && trackFilterDropdownOpen('Primary Axis')
                                                        }
                                                        onChange={(value) => {
                                                            const filteredValue = (value || []).filter(
                                                                (v: string) => !v.startsWith('__separator_'),
                                                            );
                                                            trackFilterValueChange(
                                                                'Primary Axis',
                                                                chartFilters.filters.y1,
                                                                filteredValue,
                                                            );
                                                            chartFilters.updateFilter('y1', filteredValue);
                                                        }}
                                                        options={y1GroupedOptions}
                                                        tagRender={chartMultiSelectTagRender(
                                                            chartFilters.filters.y1,
                                                            (v) =>
                                                                chartFilters.filterOptions!.y.find((y) => y.value === v)
                                                                    ?.label ?? v,
                                                        )}
                                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                                        placeholder="Select metrics..."
                                                        data-test-id={`${props['data-test-id']}-filter-y1`}
                                                        dropdownRender={(menu) =>
                                                            chartFilterDropdownRender(
                                                                menu,
                                                                {
                                                                    onReset: () => {
                                                                        trackFilterValueChange(
                                                                            'Primary Axis',
                                                                            chartFilters.filters.y1,
                                                                            [],
                                                                        );
                                                                        chartFilters.updateFilter('y1', []);
                                                                    },
                                                                    onSelectAll: () => {
                                                                        const allValues = getYAxisSelectAllValues(
                                                                            y1GroupedOptions,
                                                                            chartFilters.filters.y1,
                                                                        );
                                                                        trackFilterValueChange(
                                                                            'Primary Axis',
                                                                            chartFilters.filters.y1,
                                                                            allValues,
                                                                        );
                                                                        chartFilters.updateFilter('y1', allValues);
                                                                    },
                                                                    dataTestIdPrefix: `${props['data-test-id']}-filter-y1`,
                                                                },
                                                                { includeYAxisSeparatorStyles: true },
                                                            )
                                                        }
                                                    />
                                                </ChartFilterColumn>
                                            );
                                        })()}

                                    {/* Y2 filter (right) — only when multiple metric families exist */}
                                    {showSecondaryAxisFilter &&
                                        (() => {
                                            const y2GroupedOptions = buildY2GroupedOptions(
                                                chartFilters.filterOptions.y,
                                                chartFilters.filters.y1,
                                                chartFilters.filters.y2,
                                            );
                                            return (
                                                <ChartFilterColumn label="Secondary Axis">
                                                    <Select
                                                        mode="multiple"
                                                        value={chartFilters.filters.y2}
                                                        onDropdownVisibleChange={(open) =>
                                                            open && trackFilterDropdownOpen('Secondary Axis')
                                                        }
                                                        onChange={(value) => {
                                                            const filteredValue = (value || []).filter(
                                                                (v: string) => !v.startsWith('__separator_'),
                                                            );
                                                            trackFilterValueChange(
                                                                'Secondary Axis',
                                                                chartFilters.filters.y2,
                                                                filteredValue,
                                                            );
                                                            chartFilters.updateFilter('y2', filteredValue);
                                                        }}
                                                        options={y2GroupedOptions}
                                                        tagRender={chartMultiSelectTagRender(
                                                            chartFilters.filters.y2,
                                                            (v) =>
                                                                chartFilters.filterOptions!.y.find((y) => y.value === v)
                                                                    ?.label ?? v,
                                                        )}
                                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                                        placeholder="Select metrics..."
                                                        data-test-id={`${props['data-test-id']}-filter-y2`}
                                                        dropdownRender={(menu) =>
                                                            chartFilterDropdownRender(
                                                                menu,
                                                                {
                                                                    onReset: () => {
                                                                        trackFilterValueChange(
                                                                            'Secondary Axis',
                                                                            chartFilters.filters.y2,
                                                                            [],
                                                                        );
                                                                        chartFilters.updateFilter('y2', []);
                                                                    },
                                                                    onSelectAll: () => {
                                                                        const allValues = getYAxisSelectAllValues(
                                                                            y2GroupedOptions,
                                                                            chartFilters.filters.y2,
                                                                        );
                                                                        trackFilterValueChange(
                                                                            'Secondary Axis',
                                                                            chartFilters.filters.y2,
                                                                            allValues,
                                                                        );
                                                                        chartFilters.updateFilter('y2', allValues);
                                                                    },
                                                                    dataTestIdPrefix: `${props['data-test-id']}-filter-y2`,
                                                                },
                                                                { includeYAxisSeparatorStyles: true },
                                                            )
                                                        }
                                                    />
                                                </ChartFilterColumn>
                                            );
                                        })()}

                                    {/* Z-axis filters - Z1, Z2, Z3, etc. (similar to Y1, Y2) */}
                                    {chartFilters.filterOptions.z.length > 0 &&
                                        vizData.zAxisTitle &&
                                        vizData.zAxisTitle.map((zTitle, zIndex) => {
                                            const zOpts = chartFilters.zFilterOptions?.[zIndex];
                                            const zSelected = chartFilters.filters.z[zIndex] || [];
                                            const allZ = isZLevelAllSelected(zOpts, zSelected);
                                            const firstZ = getFirstEnabledZOptionValue(zOpts);
                                            return (
                                            <ChartFilterColumn key={`z-${zIndex}`} label={zTitle || `Z${zIndex + 1}`}>
                                                <Select
                                                    mode="multiple"
                                                    value={zSelected}
                                                    onDropdownVisibleChange={(open) =>
                                                        open &&
                                                        trackFilterDropdownOpen(zTitle || `Z${zIndex + 1}`)
                                                    }
                                                    onChange={(value) => {
                                                        trackFilterValueChange(
                                                            zTitle || `Z${zIndex + 1}`,
                                                            zSelected,
                                                            value || [],
                                                        );
                                                        chartFilters.updateFilter('z', value, zIndex);
                                                    }}
                                                    options={
                                                        zOpts?.map((z) => ({
                                                            label: z.label,
                                                            value: z.value,
                                                            disabled: z.disabled,
                                                        })) || []
                                                    }
                                                    tagRender={zAxisSelectTagRender(
                                                        zSelected,
                                                        allZ,
                                                        firstZ,
                                                        (v) => zOpts?.find((z) => z.value === v)?.label ?? v,
                                                    )}
                                                    className={CHART_FILTER_SELECT_CLASSNAME}
                                                    placeholder="Select..."
                                                    data-test-id={`${props['data-test-id']}-filter-z${zIndex + 1}`}
                                                    dropdownRender={(menu) =>
                                                        chartFilterDropdownRender(menu, {
                                                            onReset: () => {
                                                                trackFilterValueChange(
                                                                    zTitle || `Z${zIndex + 1}`,
                                                                    zSelected,
                                                                    [],
                                                                );
                                                                chartFilters.updateFilter('z', [], zIndex);
                                                            },
                                                            onSelectAll: () => {
                                                                const allValues = getSelectableOptionValues(
                                                                    zOpts ?? [],
                                                                );
                                                                trackFilterValueChange(
                                                                    zTitle || `Z${zIndex + 1}`,
                                                                    zSelected,
                                                                    allValues,
                                                                );
                                                                chartFilters.updateFilter('z', allValues, zIndex);
                                                            },
                                                            dataTestIdPrefix: `${props['data-test-id']}-filter-z${zIndex + 1}`,
                                                        })
                                                    }
                                                />
                                            </ChartFilterColumn>
                                            );
                                        })}
                                </>
                            )}
                        </ChartFilterBar>
                    </div>
                )}

                {/* Legend Section — one entry per Y × Z series; overflow via "+N More" */}
                {effectiveLegendItems.length > 0 && (
                    <div className="px-5 py-3 w-full min-w-0">
                        <ChartLegendOverflow
                            items={effectiveLegendItems}
                            data-test-id={props['data-test-id'] ? `${props['data-test-id']}-legend` : undefined}
                        />
                    </div>
                )}

                {/* Y-Axis Labels - for horizontal: primary at top, for vertical: primary left / secondary right */}
                {(yAxisLabel || y1AxisLabel || y1BarConfigs.length > 0 || y2BarConfigs.length > 0) && (
                    <div className="px-5 pt-3">
                        {horizontal ? (
                            <div className="flex justify-center">
                                {(yAxisLabel || y1AxisLabel || y1BarConfigs.length > 0) && (
                                    <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                                        {(y1AxisLabel ?? yAxisLabel) || y1BarConfigs[0]?.label || ''}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="flex justify-between">
                                {(yAxisLabel || y1AxisLabel || y1BarConfigs.length > 0) && (
                                    <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                                        {(y1AxisLabel ?? yAxisLabel) || y1BarConfigs[0]?.label || ''}
                                    </p>
                                )}
                                {y2BarConfigs.length > 0 && (
                                    <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                                        {(y2AxisLabel ?? y2BarConfigs[0]?.label) || ''}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Chart Section */}
                <div ref={chartWrapperRef} className={`px-5 py-5 ${chartClassName}`}>
                    {data.length === 0 || barsConfig.length === 0 ? (
                        <ChartNoDataState minHeight={400} data-test-id={`${props['data-test-id']}-no-data`} />
                    ) : (
                        <BarChart
                            data={transformedData}
                            barsConfig={barsConfig}
                            onChartReady={needsScroll ? handleChartReady : undefined}
                            sizeAttributes={{
                                height: 400,
                                ...(horizontal
                                    ? {
                                          paddingLeft: needsScroll
                                              ? /**
                                                 * In horizontal scrollbar mode the layout is:
                                                 *   [scrollbar] [crossPadding] [axis title + labels] [bars…]
                                                 * Reserve the full category-axis width plus a scrollbar
                                                 * column so thumbs and font variance keep clearance.
                                                 */
                                                horizontalCategoryAxisPx + HORIZONTAL_CATEGORY_SCROLLBAR_RESERVE_PX
                                              : /**
                                                 * Non-scroll: reserve the full category-axis width plus a
                                                 * small gutter. Derived from the same `horizontalCategoryAxisPx`
                                                 * as the axis `size`, so the reserved padding always contains
                                                 * the labels (fixes long-name clipping without a scrollbar).
                                                 */
                                                horizontalCategoryAxisPx + HORIZONTAL_CATEGORY_LABEL_GUTTER_PX,
                                          /**
                                           * Outer canvas padding on the right. Bar-end value labels extend
                                           * past interval geometry; extra gutter pairs with
                                           * `BAR_VALUE_LABEL_DOMAIN_PADDING` so long formatted values
                                           * are less likely to clip at the SVG edge.
                                           */
                                          paddingRight: y2BarConfigs.length > 0 ? 72 : 48,
                                          /**
                                           * Apply only a small symmetric floor on both ends of the value
                                           * axis in horizontal mode. Sizing `insetRight` to the longest
                                           * formatted positive label (or `insetLeft` to the longest
                                           * negative label) visibly drifts the value `0` tick inwards from
                                           * where the bars actually start: G2 maps the axis ticks into the
                                           * inset-shrunk data rectangle but renders the bars in the full
                                           * plot rectangle, so the two stop sharing `0` whenever the inset
                                           * grows beyond a few pixels.
                                           *
                                           * Bar value label clearance on each end is instead provided by
                                           * value-axis domain padding (`computeSingleAxisDomain` /
                                           * `computeZeroAlignedDomains`), which keeps outermost bars inside
                                           * the plot while preserving zero alignment.
                                           */
                                          insetRight: HORIZONTAL_INSET_RIGHT_FLOOR,
                                          ...(hasNegativeValues && {
                                              insetLeft: HORIZONTAL_INSET_RIGHT_FLOOR,
                                          }),
                                      }
                                    : {
                                          paddingTop: VERTICAL_BAR_LABEL_PADDING_TOP,
                                          paddingBottom: (showSlider && !showScrollbar ? 160 : 100) + 35,
                                          insetTop: VERTICAL_BAR_LABEL_INSET_TOP,
                                          /**
                                           * Mirror `insetTop` at the bottom when negatives exist so the
                                           * `position: 'bottom'` label (with `dy: 8`) sits in the inset
                                           * region instead of falling onto the x-axis line / tick labels.
                                           */
                                          ...(hasNegativeValues && {
                                              insetBottom: VERTICAL_BAR_LABEL_INSET_BOTTOM,
                                          }),
                                          ...(y2BarConfigs.length > 0 && {
                                              insetRight:
                                                  new Set(data.map((d: any) => d[xAxisKey])).size > 20 ? 65 : 55,
                                          }),
                                      }),
                                ...sizeAttributes,
                            }}
                            viewLevelAdditionalProps={{
                                ...(horizontal && {
                                    coordinate: { transform: [{ type: 'transpose' }] },
                                }),
                                ...(!horizontal &&
                                    !alignedDomains &&
                                    !singleAxisDomain && {
                                        scale: { y: { padding: BAR_VALUE_LABEL_DOMAIN_PADDING } },
                                    }),
                                theme: { fontFamily: CHART_FONT_FAMILY_CSS },
                                axis: {
                                    x: {
                                        labelFormatter: (text: string) => String(text ?? ''),
                                        // Same contract as the mark-level axis config above.
                                        ...(!horizontal && {
                                            transform: getCategoryAxisLabelTransforms(),
                                        }),
                                        title: y2BarConfigs.length > 0 ? xAxisKey : false,
                                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                                        ...(horizontal &&
                                            y2BarConfigs.length > 0 && {
                                                size: horizontalCategoryAxisPx,
                                            }),
                                        ...(horizontal && {
                                            labelAutoHide: true,
                                            labelAutoWrap: false,
                                        }),
                                        /**
                                         * Only push the bottom (value) axis tick labels away from the plot
                                         * in vertical mode. In horizontal mode the `x` channel is the
                                         * categorical axis (after `transpose`), so applying `crossPadding`
                                         * here would shove category names off-canvas. Negative bar-label
                                         * clearance in horizontal mode is handled by `insetLeft` instead.
                                         * The reduced 32 px (down from 78 px) reflects that `insetBottom`
                                         * now provides direct clearance for the negative bar value label.
                                         */
                                        ...(hasNegativeValues && !horizontal && {
                                            crossPadding: 32,
                                            labelSpacing: 12,
                                        }),
                                    },
                                    y: {
                                        ...(horizontal && { position: 'top' as const }),
                                        labelFormatter: (value: number) => formatNumWithKMB(value),
                                        title: false,
                                        grid: true,
                                        gridLineWidth: 1,
                                        gridStroke: 'rgba(0, 0, 0, 0.06)',
                                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                                    },
                                },
                                legend: false,
                                ...viewLevelAdditionalProps,
                                interaction: {
                                    ...getChartColumnHighlightInteraction(),
                                    ...viewLevelAdditionalProps?.interaction,
                                    tooltip: {
                                        ...getAggregatedChartTooltipInteraction(),
                                        ...viewLevelAdditionalProps?.interaction?.tooltip,
                                    },
                                    /**
                                     * G2's `sliderFilter` / `scrollbarFilter` interactions default to
                                     * `adaptiveMode: 'filter'`, which on every scroll/slider change
                                     * recomputes the value-axis (channel-1) domain from the data
                                     * currently visible inside the scroll window and merges it back
                                     * onto the mark's scale — silently overriding the explicit
                                     * `scale.y.domain` set above (`alignedDomains.y1` /
                                     * `singleAxisDomain`). The result was that the y-scale "jumped"
                                     * each time the user dragged the scrollbar/slider in single-axis
                                     * mode (the dual-axis path is unaffected because its `independent`
                                     * y2 mark routes filtering through the multi-axis branch).
                                     *
                                     * Disabling adaptive mode on both interactions makes scrolling a
                                     * pure pan operation: the data window changes, but the value-axis
                                     * scale stays pinned to the full-dataset domain we computed.
                                     */
                                    sliderFilter: {
                                        ...((viewLevelAdditionalProps?.interaction as any)?.sliderFilter ?? {}),
                                        adaptiveMode: false,
                                    },
                                    scrollbarFilter: {
                                        ...((viewLevelAdditionalProps?.interaction as any)?.scrollbarFilter ?? {}),
                                        adaptiveMode: false,
                                    },
                                },
                                /**
                                 * G2's x-slider interaction (`sliderFilter`) merges `clip: options.clip ?? true`
                                 * while filtering — enabling a plot clipPath that trims bar value labels at the top.
                                 * Setting `clip: false` keeps labels visible after moving the scroller.
                                 */
                                clip: false,
                            }}
                            additionalMarks={additionalMarks}
                            barClickHandler={onBarClick}
                            data-test={`${props['data-test']}-chart`}
                        />
                    )}
                </div>

                {/* Range Slider - filters which bars are included from the full dataset */}
                {showScrollbar && showSlider && data.length > 0 && totalCategories > 1 && !horizontal && (
                    <div className="px-5 pb-1" data-export-exclude>
                        <Slider
                            range
                            min={0}
                            max={totalCategories - 1}
                            value={sliderRange}
                            onChange={handleSliderChange}
                            tooltip={{
                                formatter: (val?: number) =>
                                    val !== undefined && uniqueCategories[val] ? String(uniqueCategories[val]) : '',
                            }}
                            styles={{
                                track: { background: '#5B50D1' },
                                rail: { background: '#f0f0f0' },
                            }}
                            data-test-id={`${props['data-test-id']}-scroll-slider`}
                        />
                    </div>
                )}

                {/* Secondary axis label below chart (horizontal mode with dual axis) */}
                {horizontal && y2BarConfigs.length > 0 && (
                    <div className="px-5 pt-2 flex justify-center">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {(y2AxisLabel ?? y2BarConfigs[0]?.label) || ''}
                        </p>
                    </div>
                )}

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default EnhancedBarChart;
